import apiClient from './apiClient';

export interface Account {
  id: string;
  name: string;
  domain: string | null;
  accountType?: string;
  partnerType?: string | null;
  partnerTerritory?: string | null;
  partnerMarketFocus?: string | null;
  partnerPortfolio?: string | null;
  partnerDeliveryCapacity?: string | null;
  partnerDealModel?: string | null;
  industryVertical: string | null;
  subVertical: string | null;
  region: string | null;
  plantType: string | null;
  abmTier: number | null;
  maturityBand: string | null;
  maturityConfidence: string | null;
  primaryPainAxis: string | null;
  painDescription?: string | null;
  additionalNotes?: string | null;
  architectureTags: string[];
  dataReadinessLevel: string | null;
  automationStage: string | null;
  buyingGroupStatus: any | null;
  status: string;
  assignedMdaId: string | null;
  createdAt: string;
  updatedAt: string;
  assignedMda?: {
    id: string;
    name: string;
    email: string;
    role: string;
  } | null;
  contacts?: any[];
  mqlSubmissions?: any[];
}

export interface AccountsResponse {
  data: Account[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateAccountData {
  name: string;
  industry: string;
  employee_count: number;
  annual_revenue: number;
  website?: string;
  tier: 'Tier1' | 'Tier2' | 'Tier3';
  status: 'Active' | 'Inactive';
}

export interface AccountTimelineEvent {
  id: string;
  type: 'CALL' | 'MAGNET_DELIVERY' | 'ENGAGEMENT';
  subType?: string;
  timestamp: string;
  title: string;
  description: string;
  contactId: string;
  contactName: string;
  metadata: Record<string, any>;
  icon: string;
  color: string;
}

export interface AccountTimelineResponse {
  data: AccountTimelineEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalCalls: number;
    totalDeliveries: number;
    totalEngagements: number;
    totalEvents: number;
    totalContacts: number;
    contactEngagement: Record<string, {
      name: string;
      calls: number;
      deliveries: number;
      engagements: number;
    }>;
  };
}

/**
 * Get all accounts with optional filters
 */
export const getAccounts = async (params?: {
  search?: string;
  tier?: string;
  status?: string;
  accountType?: string;
  page?: number;
  limit?: number;
}): Promise<AccountsResponse> => {
  const response = await apiClient.get<AccountsResponse>('/api/accounts', { params });
  return response.data;
};

/**
 * Get single account by ID
 */
export const getAccount = async (id: string): Promise<Account> => {
  const response = await apiClient.get<Account>(`/api/accounts/${id}`);
  return response.data;
};

/**
 * Get account interaction timeline (aggregated across all contacts)
 */
export const getAccountTimeline = async (
  id: string,
  params?: {
    page?: number;
    limit?: number;
  }
): Promise<AccountTimelineResponse> => {
  const response = await apiClient.get<AccountTimelineResponse>(`/api/accounts/${id}/timeline`, { params });
  return response.data;
};

/**
 * Create new account
 */
export const createAccount = async (data: CreateAccountData): Promise<Account> => {
  const response = await apiClient.post<Account>('/api/accounts', data);
  return response.data;
};

/**
 * Update account
 */
export const updateAccount = async (id: string, data: Partial<CreateAccountData>): Promise<Account> => {
  const response = await apiClient.put<Account>(`/api/accounts/${id}`, data);
  return response.data;
};

/**
 * Delete account
 */
export const deleteAccount = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/accounts/${id}`);
};
